// PeerJS-shaped wrapper around one or more raw `RTCDataChannel`s.
//
// HostStreamer + P2pSession both bind to `PeerHub`'s EventTarget API
// (dispatches 'peer' events with PeerEvent payloads, exposes
// broadcast / sendTo / sendToHost / peerIds). When pairing happens
// over QR-based manual signalling we don't have PeerJS at all, just
// bare RTCDataChannels. This class adapts those to the same surface
// so the streamer / scheduler / clock-sync code paths plug in
// unchanged.

import type { PeerEvent } from './PeerHub'

interface ChannelEntry {
  peerId: string
  dc: RTCDataChannel
  pc?: RTCPeerConnection
}

let nextOfflinePeerId = 1

export class OfflinePeerHub extends EventTarget {
  private channels = new Map<string, ChannelEntry>()

  /** Adopt an already-open (or about-to-open) DataChannel. The
   *  `peerId` is opaque — used for routing sendTo() calls and
   *  bookkeeping. If omitted, an incrementing local label is
   *  generated. */
  adopt(dc: RTCDataChannel, opts?: { peerId?: string; pc?: RTCPeerConnection }): string {
    const peerId = opts?.peerId ?? `offline-${nextOfflinePeerId++}`
    dc.binaryType = 'arraybuffer'
    const entry: ChannelEntry = { peerId, dc, pc: opts?.pc }
    this.channels.set(peerId, entry)

    const wireUp = () => {
      this.emit({ type: 'connected', peerId })
      dc.addEventListener('message', (ev) => this.handleMessage(peerId, ev.data))
      dc.addEventListener('close', () => this.handleClose(peerId))
      dc.addEventListener('error', () => this.handleClose(peerId))
    }
    if (dc.readyState === 'open') {
      // Emit asynchronously so callers attaching listeners synchronously
      // after `adopt()` don't miss the 'connected' event.
      queueMicrotask(wireUp)
    } else {
      dc.addEventListener('open', wireUp, { once: true })
    }
    return peerId
  }

  sendTo(peerId: string, buffer: ArrayBuffer): void {
    const c = this.channels.get(peerId)
    if (c && c.dc.readyState === 'open') c.dc.send(buffer)
  }

  broadcast(buffer: ArrayBuffer): void {
    for (const c of this.channels.values()) {
      if (c.dc.readyState === 'open') c.dc.send(buffer)
    }
  }

  sendToHost(buffer: ArrayBuffer): void {
    const first = this.channels.values().next().value as ChannelEntry | undefined
    if (first && first.dc.readyState === 'open') first.dc.send(buffer)
  }

  peerIds(): string[] {
    return Array.from(this.channels.keys())
  }

  destroy(): void {
    for (const c of this.channels.values()) {
      try { c.dc.close() } catch { /* noop */ }
      try { c.pc?.close() } catch { /* noop */ }
    }
    this.channels.clear()
  }

  // ─── Internals ───────────────────────────────────────────────────────

  private handleMessage(peerId: string, raw: unknown) {
    let buf: ArrayBuffer | null = null
    if (raw instanceof ArrayBuffer) {
      buf = raw
    } else if (raw instanceof Uint8Array) {
      const copy = new Uint8Array(raw.byteLength)
      copy.set(raw)
      buf = copy.buffer
    } else if (ArrayBuffer.isView(raw)) {
      const view = raw as ArrayBufferView
      const copy = new Uint8Array(view.byteLength)
      copy.set(new Uint8Array(view.buffer, view.byteOffset, view.byteLength))
      buf = copy.buffer
    } else if (typeof Blob !== 'undefined' && raw instanceof Blob) {
      raw.arrayBuffer().then((ab) => this.emit({ type: 'data', peerId, data: ab }))
        .catch(() => { /* swallow */ })
      return
    }
    if (buf) this.emit({ type: 'data', peerId, data: buf })
  }

  private handleClose(peerId: string) {
    const entry = this.channels.get(peerId)
    if (!entry) return
    this.channels.delete(peerId)
    try { entry.pc?.close() } catch { /* noop */ }
    this.emit({ type: 'disconnected', peerId })
  }

  private emit(ev: PeerEvent) {
    this.dispatchEvent(new CustomEvent<PeerEvent>('peer', { detail: ev }))
  }
}
